import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Empty, Modal, Form, Input, Select, InputNumber, Switch, Tag, Space,
  Table, message, Popconfirm, Tooltip
} from 'antd';
import {
  PlusOutlined, StarOutlined, StarFilled, EditOutlined, DeleteOutlined
} from '@ant-design/icons';
import { itemsApi } from '../../itemsApi';
import {
  STORAGE_PROVIDERS, IMAGE_ROLES, ALLOWED_IMAGE_MIME_TYPES,
  ALLOWED_IMAGE_FILE_EXTS, humaniseEnum
} from '../../studioConstants';

// ─────────────────────────────────────────────────────────────────
// Step 6 — Images / media (metadata-only).
//
// This stage stores image metadata only — no file upload, no
// DigitalOcean Spaces calls. The user enters storage_key manually
// (or pastes an external_url) so the metadata row exists; the next
// stage will hook in the actual upload pipeline.
// ─────────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  storageProvider: 'digitalocean_spaces',
  storageKey: '',
  publicUrl: '',
  originalFileName: '',
  mimeType: undefined,
  fileExt: undefined,
  fileSizeBytes: null,
  imageWidthPx: null,
  imageHeightPx: null,
  imageRole: 'gallery',
  altText: '',
  caption: '',
  displayOrder: 0,
  isPrimary: false,
  isVisibleOnline: true,
  isVisibleInternal: true,
  status: 'active'
};

export default function ItemImagesStep({ disabled, itemId, onImageCountChange }) {
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(false);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState('add');
  const [editorRow, setEditorRow]   = useState(null);
  const [form] = Form.useForm();

  const refresh = useCallback(async () => {
    if (!itemId) { setRows([]); return; }
    setLoading(true);
    try {
      const data = await itemsApi.listImages(itemId);
      const list = Array.isArray(data && data.rows) ? data.rows
        : (Array.isArray(data) ? data : []);
      setRows(list);
      if (typeof onImageCountChange === 'function') onImageCountChange(list.length);
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to load images.');
    } finally {
      setLoading(false);
    }
  }, [itemId, onImageCountChange]);

  useEffect(() => { refresh(); }, [refresh]);

  const openAdd = () => {
    setEditorMode('add'); setEditorRow(null);
    form.resetFields(); form.setFieldsValue(EMPTY_FORM);
    setEditorOpen(true);
  };
  const openEdit = (record) => {
    setEditorMode('edit'); setEditorRow(record);
    form.resetFields();
    form.setFieldsValue({
      ...EMPTY_FORM,
      ...record
    });
    setEditorOpen(true);
  };
  const closeEditor = () => setEditorOpen(false);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = { ...values };
      if (editorMode === 'add') {
        await itemsApi.createImage(itemId, payload);
        message.success('Image metadata added.');
      } else {
        await itemsApi.updateImage(itemId, editorRow.id, payload);
        message.success('Image metadata updated.');
      }
      setEditorOpen(false);
      await refresh();
    } catch (e) {
      if (e && e.errorFields) return;
      message.error(e?.response?.data?.error?.message || 'Failed to save image.');
    }
  };

  const handleSetPrimary = async (record) => {
    try {
      await itemsApi.setImagePrimary(itemId, record.id);
      message.success('Primary image updated.');
      await refresh();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to set primary.');
    }
  };

  const handleArchive = async (record) => {
    try {
      await itemsApi.deleteImage(itemId, record.id);
      message.success('Image archived.');
      await refresh();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to archive image.');
    }
  };

  const columns = [
    {
      title: '#', dataIndex: 'displayOrder', width: 60,
      render: (v) => (v === null || v === undefined) ? '—' : v
    },
    {
      title: 'Role', dataIndex: 'imageRole', width: 110,
      render: (v) => <Tag color={v === 'primary' ? 'blue' : 'default'}>{humaniseEnum(v)}</Tag>
    },
    {
      title: 'Storage', dataIndex: 'storageProvider', width: 150,
      render: (v) => humaniseEnum(v)
    },
    { title: 'Storage key', dataIndex: 'storageKey', ellipsis: true },
    {
      title: 'Primary', dataIndex: 'isPrimary', width: 80,
      render: (v) => v
        ? <StarFilled style={{ color: '#FAAD14' }} />
        : <span style={{ color: '#94A3B8' }}>—</span>
    },
    {
      title: 'Status', dataIndex: 'status', width: 100,
      render: (v) => <Tag color={v === 'active' ? 'green' : (v === 'archived' ? 'default' : 'orange')}>
        {humaniseEnum(v)}
      </Tag>
    },
    {
      title: '', key: 'actions', width: 150,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Mark as primary">
            <Button
              size="small" type="text"
              icon={<StarOutlined />}
              disabled={disabled || record.status === 'archived' || record.isPrimary}
              onClick={() => handleSetPrimary(record)}
            />
          </Tooltip>
          <Tooltip title="Edit metadata">
            <Button
              size="small" type="text"
              icon={<EditOutlined />}
              disabled={disabled}
              onClick={() => openEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Archive this image?"
            description="The metadata row stays for audit; status flips to archived."
            okText="Archive"
            cancelText="Cancel"
            onConfirm={() => handleArchive(record)}
            disabled={disabled}
          >
            <Button
              size="small" type="text" danger
              icon={<DeleteOutlined />}
              disabled={disabled}
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <h2 className="erp-studio-section-title">Images & media</h2>
      <p className="erp-studio-section-help">
        This stage stores image metadata only. Actual file upload integration (DigitalOcean
        Spaces, cropping, thumbnails) will be added in the next stage.
      </p>

      <Alert
        type="info" showIcon
        style={{ marginBottom: 12, fontSize: 12.5 }}
        message="Metadata-only mode"
        description="Paste a storage key (object-store path) or external image URL. Saving here does not upload any file. The Primary image is enforced at most one active per item."
      />

      {!itemId ? (
        <div className="erp-studio-empty-hint">
          Save the basic item first to start adding image metadata. The list and edit actions
          unlock as soon as the item exists in the database.
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openAdd}
              disabled={disabled}
            >
              Add image
            </Button>
          </div>

          <Table
            size="small"
            rowKey="id"
            columns={columns}
            dataSource={rows}
            loading={loading}
            pagination={false}
            locale={{
              emptyText: <Empty description="No image metadata yet." image={Empty.PRESENTED_IMAGE_SIMPLE} />
            }}
          />
        </>
      )}

      <Modal
        open={editorOpen}
        title={editorMode === 'add' ? 'Add image metadata' : 'Edit image metadata'}
        onCancel={closeEditor}
        onOk={handleSave}
        okText={editorMode === 'add' ? 'Add' : 'Save'}
        cancelText="Cancel"
        width={720}
        destroyOnClose
        forceRender
      >
        <Alert
          type="warning" showIcon
          style={{ marginBottom: 12, fontSize: 12 }}
          message="No file is uploaded by this dialog — only metadata is recorded."
        />
        <Form form={form} layout="vertical" requiredMark={false} preserve={false}>
          <Form.Item
            label="Storage provider" name="storageProvider"
            rules={[{ required: true }]}
          >
            <Select options={STORAGE_PROVIDERS.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
          </Form.Item>
          <Form.Item
            label="Storage key (or external URL)"
            name="storageKey"
            rules={[
              { required: true, message: 'Storage key is required.' },
              { max: 2048 }
            ]}
            extra="Object-store path for digitalocean_spaces / local, or a fully-qualified URL for external_url."
          >
            <Input placeholder="tenants/&lt;tid&gt;/items/&lt;iid&gt;/primary.jpg or https://…" />
          </Form.Item>
          <Form.Item label="Public URL" name="publicUrl" rules={[{ max: 2048 }, { type: 'url', message: 'Must be a full URL.' }]}>
            <Input placeholder="Optional CDN URL." />
          </Form.Item>
          <Form.Item label="Image role" name="imageRole" rules={[{ required: true }]}>
            <Select options={IMAGE_ROLES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
          </Form.Item>
          <Form.Item label="MIME type" name="mimeType">
            <Select allowClear
              options={ALLOWED_IMAGE_MIME_TYPES.map((s) => ({ value: s, label: s }))} />
          </Form.Item>
          <Form.Item label="File extension" name="fileExt">
            <Select allowClear
              options={ALLOWED_IMAGE_FILE_EXTS.map((s) => ({ value: s, label: s }))} />
          </Form.Item>
          <Form.Item label="File size (bytes)" name="fileSizeBytes">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Width (px)" name="imageWidthPx">
            <InputNumber min={0} max={100000} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Height (px)" name="imageHeightPx">
            <InputNumber min={0} max={100000} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Alt text" name="altText" rules={[{ max: 180 }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Caption" name="caption" rules={[{ max: 255 }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Display order" name="displayOrder">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Mark as primary" name="isPrimary" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="Visible online" name="isVisibleOnline" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="Visible internal" name="isVisibleInternal" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
